import { DateBadge } from "../dashboard/DateBadge";
import { PersonBadge } from "../dashboard/PersonBadge";
import { useState, useCallback, useMemo, useEffect } from "react";
import type { AddNoteBody } from "@/interfaces/estimation.interface";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const MobileScopeNote = ({ estimateId, note, editable = false }: { estimateId: string; note: AddNoteBody; editable?: boolean }) => {
  const { updateOrCreateScopeNoteObject, isUpdateScopeNoteLoading } = useEstimateDetail(estimateId);

  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (!isEditing && !isUpdating && note.text !== noteText) {
      setNoteText(note.text ?? "");
    }
  }, [isEditing, note.text]);

  const handleCancel = useCallback(() => {
    setNoteText(note.text ?? "");
    setIsEditing(false);
  }, [noteText]);

  const handleSave = useCallback(async () => {
    setShowConfirmDialog(true);
  }, []);

  const handleConfirmSave = useCallback(async () => {
    setShowConfirmDialog(false);
    updateOrCreateScopeNoteObject({
      ...note,
      text: noteText,
    });
    setIsUpdating(true);
    setIsEditing(false);
  }, [note, noteText]);

  const handleCancelSave = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  useEffect(() => {
    if (isUpdating && !isUpdateScopeNoteLoading) {
      toast.info(`Successfully update note!`);
      setIsUpdating(false);
      setIsEditing(false);
    }
  }, [isUpdating, isUpdateScopeNoteLoading]);

  const isNoteTextChanged = useMemo(() => noteText !== note.text, [noteText, note.text]);

  return (
    <div className="relative mb-3 rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <DateBadge date={note.dateCreated} showTime={true} />
        <div className="flex justify-end">
          {/* temporary: disable editing mobile scope notes */}
          {/* {editable && !isEditing && !isUpdating && (
            <button onClick={handleStartEdit} className="hover:bg-gray-100 rounded transition-colors duration-200 p-2">
              <FiEdit3 className="w-5 h-5 text-black" />
            </button>
          )} */}
          {/* `editable` is true only for the most recent scope note, use this to only show spinning on top-most scope note */}
          {editable && isUpdateScopeNoteLoading && (
            <div className="my-2 mr-1 ml-10 h-5 w-5 animate-spin rounded-full border-b-2 border-gray-600"></div>
          )}
        </div>
      </div>
      <PersonBadge id={note.createdByUserId} />
      {editable && isEditing ? (
        <>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            className="mt-2 w-full resize-none rounded-md border border-gray-300 bg-gray-50 p-3 text-sm leading-relaxed text-gray-700 focus:border-black focus:outline-none"
            style={{ fontSize: "16px" }}
            rows={Math.max(3, noteText.split("\n").length)}
            autoFocus
          />
          <div className="mt-3 ml-1 flex justify-end gap-2">
            <button
              onClick={handleCancel}
              className="rounded-md bg-gray-100 px-3 py-1.5 text-sm text-red-600 transition-colors duration-200 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isNoteTextChanged || isUpdating}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors duration-200 ${
                isNoteTextChanged && !isUpdating ? "bg-black text-white hover:bg-gray-800" : "cursor-not-allowed bg-gray-100 text-gray-400"
              }`}
            >
              Save
            </button>
          </div>
        </>
      ) : (
        <div className="mt-2 ml-1 text-sm leading-relaxed whitespace-pre-wrap text-gray-700">{noteText}</div>
      )}

      {/* Confirmation Dialog */}
      {editable && (
        <AlertDialog
          open={showConfirmDialog}
          onOpenChange={() => {
            handleCancelSave();
          }}
        >
          <AlertDialogContent className="!bg-white text-black">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lg font-medium text-gray-800">Confirm Changes?</AlertDialogTitle>
              <AlertDialogDescription className="mt-2 text-sm text-gray-600">
                If confirmed, the entire estimate will be re-generated based on these notes and any changes that have been made to the
                estimate will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-2 flex gap-2">
              <AlertDialogAction
                onClick={() => handleConfirmSave()}
                className="!h-12 !bg-black !px-6 !py-3 !text-sm !text-white hover:!bg-red-700"
              >
                Confirm
              </AlertDialogAction>
              <AlertDialogAction onClick={() => handleCancelSave()} className="!h-12 !px-6 !py-3 !text-sm !text-red-600 hover:!bg-red-700">
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};
